import { useLayoutEffect, type ComponentType } from 'react'
// NOTE: RouterProvider comes from the ROOT entry deliberately — importing it
// from 'react-router/dom' loads a SECOND copy of the react-router module under
// vitest (ESM/CJS split), whose Router context the components' 'react-router'
// imports cannot see ("useLocation() may be used only in the context of a
// <Router>"). One entry point = one instance everywhere.
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router'
import { RequireAnon, RequireRole } from '@/components/auth/require-role'
import { RouteErrorBoundary } from '@/components/common/error-boundary'
import { AppShell } from '@/components/layout/app-shell'
import {
  homeForRoles,
  ingestJwtTokenFromUrl,
  useSession,
} from '@/lib/mobility-auth'
import { setToken } from '@/lib/token-store'
import { BASE_PATH } from '@/lib/base-path'

/**
 * The mobility SPA router (manual createBrowserRouter, react-router v7).
 *
 * Route map (ground rules / API_SPEC §13):
 *   /               → session-aware redirect (signed out → /login, else homeForRoles)
 *   /login          → RequireAnon (a signed-in principal bounces to their home)
 *   /home
 *   /jobs
 *   /applications   → RequireRole [EMPLOYEE] + AppShell (EMPLOYEE is implicit for
 *   /hr             → RequireRole [HR_PARTNER]    + AppShell
 *   /recruiter      → RequireRole [RECRUITER]     + AppShell
 *   /admin          → RequireRole [HR_ADMIN] + AppShell
 *   *               → /
 *
 * Every PAGE is code-split via the route `lazy` field; the guards + shell are
 * eager (they render on every route). Portal agents add child routes under the
 * matching subtree — the guard + shell wrappers here stay untouched.
 */

/**
 * `?JWT_TOKEN=` on ANY route (full load or in-app navigation) becomes the
 * session bearer. Capture at module load so a hard landing is stripped before
 * the router paints; the layout below captures later navigations the same way.
 * The API verifies RS256 (Thrive public key) itself.
 */
ingestJwtTokenFromUrl()

/**
 * TEST-ONLY seam: re-arm a `JWT_TOKEN` as if it had just arrived on the URL.
 * Never called in app code.
 */
export function armSsoBootstrapForTests(token: string | null): void {
  if (token) setToken(token)
}

/** Adapt a default-export page module to a react-router lazy route module. */
const page =
  (loader: () => Promise<{ default: ComponentType }>) =>
  async () => ({ Component: (await loader()).default })

/** `/` — resolve the session once, then land on the principal's role home. */
function HomeRedirect() {
  const { loading, signedIn, roles } = useSession()
  if (loading) return <BootFallback />
  if (!signedIn) return <Navigate to="/login" replace />
  return <Navigate to={homeForRoles(roles)} replace />
}

const AXIS_DEMO_UI =
  Boolean(import.meta.env.DEV) || import.meta.env.VITE_AXIS_DEMO_ENABLED === 'true'

/** Content-free, accessible splash shown while a lazy chunk / the session loads. */
function BootFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-screen flex items-center justify-center text-muted-foreground text-sm"
    >
      Loading…
    </div>
  )
}

export const router = createBrowserRouter([
  {
    hydrateFallbackElement: <BootFallback />,
    // UI-1: ONE boundary, on the root — react-router bubbles a throw to the
    // nearest ancestor errorElement, so this catches every route below it
    // (including a lazy chunk that 404s after a redeploy). Deliberately not
    // repeated on the AppShell subtrees: the root element is a bare
    // Outlet wrapper with no chrome of its own, so there is no nav worth
    // preserving HERE, and a per-shell boundary would keep a nav rendered
    // around a page whose data we no longer trust. "Back to home" is the
    // way back in.
    errorElement: <RouteErrorBoundary />,
    element: <JwtTokenQueryCapture />,
    children: [
      { index: true, element: <HomeRedirect /> },
      ...(AXIS_DEMO_UI
        ? [
            { path: 'demo/axis-demo', lazy: page(() => import('@/app/dev/axis-demo/page')) },
            { path: 'dev/axis-demo', element: <Navigate to="/demo/axis-demo" replace /> },
          ]
        : []),
      {
        path: 'login',
        element: <RequireAnon />,
        children: [{ index: true, lazy: page(() => import('@/app/login/page')) }],
      },
      {
        // Employee portal — EMPLOYEE is implicit, so any signed-in principal passes.
        element: <RequireRole allow={['EMPLOYEE']} />,
        children: [
          {
            element: <AppShell />,
            children: [
              { path: 'home', lazy: page(() => import('@/app/home/page')) },
              { path: 'recommendations', lazy: page(() => import('@/app/recommendations/page')) },
              // DISCOVER — intent search, always the index. Separate from /jobs
              // on purpose: one page is literal SQL, the other is Meilisearch,
              // and blending them made neither legible (see the page header).
              { path: 'discover', lazy: page(() => import('@/app/discover/page')) },
              { path: 'jobs', lazy: page(() => import('@/app/jobs/page')) },
              { path: 'jobs/:id', lazy: page(() => import('@/app/jobs/[id]/page')) },
              { path: 'applications', lazy: page(() => import('@/app/applications/page')) },
            ],
          },
        ],
      },
      {
        path: 'hr',
        element: <RequireRole allow={['HR_PARTNER']} />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, lazy: page(() => import('@/app/hr/page')) },
              { path: 'uploads', lazy: page(() => import('@/app/hr/uploads/page')) },
            ],
          },
        ],
      },
      {
        // HR_ADMIN added as a stopgap (FE2-10): the backend admits admins
        // on every recruiter endpoint, so mixed-role orgs work either way.
        // HR_PARTNER added for Wave US (US-2/US-D3): any staff role can be a
        // requisition's ASSIGNEE — the backend scopes their lists server-side,
        // so an unassigned HR Partner simply sees friendly empty lists.
        path: 'recruiter',
        element: <RequireRole allow={['HR_PARTNER', 'HR_ADMIN']} />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, lazy: page(() => import('@/app/recruiter/page')) },
              {
                path: 'requisitions/:id',
                lazy: page(() => import('@/app/recruiter/requisitions/[id]/page')),
              },
              {
                path: 'applications',
                lazy: page(() => import('@/app/recruiter/applications/page')),
              },
            ],
          },
        ],
      },
      {
        path: 'admin',
        element: <RequireRole allow={['HR_ADMIN']} />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, lazy: page(() => import('@/app/admin/page')) },
              {
                path: 'requisitions/:id',
                lazy: page(() => import('@/app/admin/requisitions/[id]/page')),
              },
              {
                path: 'applications',
                lazy: page(() => import('@/app/admin/applications/page')),
              },
              { path: 'entities', lazy: page(() => import('@/app/admin/entities/page')) },
              { path: 'grades', lazy: page(() => import('@/app/admin/grades/page')) },
              { path: 'roster', lazy: page(() => import('@/app/admin/roster/page')) },
              // FB-11: the IJP base (the eligibility allow-list) as its own surface.
              { path: 'ijp-base', lazy: page(() => import('@/app/admin/ijp-base/page')) },
              // FB-6: the centrally managed badge catalogue.
              { path: 'badges', lazy: page(() => import('@/app/admin/badges/page')) },
              // WAVE M1: the department master — the authoring vocabulary.
              { path: 'departments', lazy: page(() => import('@/app/admin/departments/page')) },
              // WAVE M6 — the configurable application lifecycle.
              { path: 'statuses', lazy: page(() => import('@/app/admin/statuses/page')) },
              // WAVE M2: the JOB-ROLE master (role_id → job description). A
              // different concept from `roles` below, which grants ACCESS —
              // hence the distinct path rather than a nested route under it.
              { path: 'role-master', lazy: page(() => import('@/app/admin/role-master/page')) },
              // WAVE F3: the SKILL master — the taxonomy every job description is
              // matched against. Beside the other two authoring masters because
              // it is the third leg of the same vocabulary, and the only one
              // whose rows are not usable until they have been embedded.
              { path: 'skill-master', lazy: page(() => import('@/app/admin/skill-master/page')) },
              { path: 'recommender', lazy: page(() => import('@/app/admin/recommender/page')) },
              { path: 'roles', lazy: page(() => import('@/app/admin/roles/page')) },
              {
                path: 'match-policy',
                lazy: page(() => import('@/app/admin/match-policy/page')),
              },
              { path: 'settings', lazy: page(() => import('@/app/admin/settings/page')) },
            ],
          },
        ],
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
], {
  // Under a subpath deploy the browser's path carries the mount prefix
  // (/group-mobility/jobs/1) while every `path` above is written from the
  // app's own root (jobs/1). basename is what reconciles the two — without it
  // NOTHING matches and every route falls through to the `*` redirect, which
  // looks like a blank app rather than a routing misconfiguration.
  // '/' for a root deploy, i.e. unchanged. See lib/base-path.ts.
  basename: BASE_PATH || '/',
})

function JwtTokenQueryCapture() {
  const location = useLocation()
  const captured = ingestJwtTokenFromUrl()
  useLayoutEffect(() => {
    if (captured) setToken(captured)
  }, [captured, location.pathname, location.search])
  return <Outlet />
}

export default function App() {
  return <RouterProvider router={router} />
}
